import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import type { OptimizationReport } from '@monitoring-panel/types';
import { usePersistentState } from '@/hooks/usePersistentState';
import { useOptimizationReport, useOptimizationReports } from '@/hooks/useOptimizationReports';
import { cn } from '@/lib/utils';
import { CardLineMetrics } from './optimizations/CardLineMetrics';
import { CardSuggestions } from './optimizations/CardSuggestions';
import { fullDate } from './optimizations/format';
import { asDeliveryReport } from './optimizations/types';
import { Skeleton } from './Skeleton';

const TABS = [
  { id: 'charts', label: 'Gráficos' },
  { id: 'optimizations', label: 'Optimizaciones' },
  { id: 'lines', label: 'Por línea' },
] as const;

type TabId = (typeof TABS)[number]['id'];

const TAB_IDS: readonly string[] = TABS.map((tab) => tab.id);

interface CardSidePanelProps {
  executionId: string;
  /** Only fetch once the card is on screen. */
  enabled: boolean;
  /** The dashboard chart preview, shown in the Gráficos tab. */
  charts: ReactNode;
}

/**
 * Right side of an execution card in wide view. The chosen tab is shared by
 * every card and remembered, so a trader reviewing optimizations sees them on
 * each execution as they scroll. The panel takes the height of the card's
 * left side and scrolls inside it.
 */
export function CardSidePanel({ executionId, enabled, charts }: CardSidePanelProps) {
  const [activeTab, setActiveTab] = usePersistentState<TabId>(
    'card-panel-tab',
    'charts',
    (value) => typeof value === 'string' && TAB_IDS.includes(value)
  );

  const reportsQuery = useOptimizationReports(enabled ? executionId : '');
  const latest = reportsQuery.data?.reports[0] ?? null;
  const needsReport = activeTab !== 'charts';
  const reportQuery = useOptimizationReport(
    enabled && needsReport ? executionId : '',
    latest?.reportDate ?? null
  );

  return (
    <div className="lg:flex-1 lg:min-w-0 relative min-h-[320px] border-t lg:border-t-0 border-adv-border">
      <div className="lg:absolute lg:inset-0 flex flex-col">
        <nav className="adv-tabs flex-shrink-0 px-2" role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn('adv-tab', activeTab === tab.id && 'adv-tab--active')}
            >
              {tab.label}
              {tab.id === 'optimizations' && latest && latest.pending > 0 && (
                <span className="adv-tag adv-tag--default adv-num ml-2">{latest.pending}</span>
              )}
            </button>
          ))}
        </nav>

        <div className="flex-1 min-h-0 overflow-y-auto p-3">
          {activeTab === 'charts' ? (
            charts
          ) : reportsQuery.isLoading || reportQuery.isLoading ? (
            <PanelSkeleton />
          ) : reportsQuery.error || reportQuery.error ? (
            <div className="adv-alert text-adv-body-s">
              No se pudo cargar el informe de optimización: {(reportsQuery.error ?? reportQuery.error)?.message}
            </div>
          ) : !reportQuery.data?.report ? (
            <NoReport executionId={executionId} />
          ) : (
            <ReportTab tabId={activeTab} executionId={executionId} report={reportQuery.data.report} />
          )}
        </div>
      </div>
    </div>
  );
}

interface ReportTabProps {
  tabId: Exclude<TabId, 'charts'>;
  executionId: string;
  report: OptimizationReport;
}

function ReportTab({ tabId, executionId, report }: ReportTabProps) {
  const navigate = useNavigate();
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 text-adv-body-s text-adv-tertiary">
        <span>
          Informe del <span className="adv-num">{fullDate(report.reportDate)}</span> ·{' '}
          <span className="adv-num">{report.pending}</span> pendientes de{' '}
          <span className="adv-num">{report.suggestions}</span>
        </span>
        <button
          type="button"
          onClick={() => navigate(`/campaigns/${executionId}`)}
          className="adv-link inline-flex items-center gap-1 flex-shrink-0"
        >
          Informe completo
          <ArrowRight size={12} />
        </button>
      </div>
      {tabId === 'optimizations' ? (
        <CardSuggestions executionId={executionId} report={report} />
      ) : (
        <CardLineMetrics report={asDeliveryReport(report.report)} reportDate={report.reportDate} />
      )}
    </div>
  );
}

function NoReport({ executionId }: { executionId: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-full min-h-[160px] text-center text-adv-tertiary px-6">
      <Sparkles size={24} className="mb-2 opacity-50" />
      <p className="text-adv-body-s">
        Aún no hay informe de optimización para {executionId}. Aparece cuando alguien ejecuta{' '}
        <code className="adv-num text-adv-primary">/vera:delivery-report</code>.
      </p>
    </div>
  );
}

function PanelSkeleton() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-4 w-1/2" />
      <Skeleton className="h-12 w-full" />
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-16 w-full" />
    </div>
  );
}
